/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { Edit } from '@jda/lui-common-icon-library-mui5';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import { LuiCard } from '@jda/lui-common-component-library-mui5';

import { useStyles } from './styles';
import SettingsFormDialog, { SettingsField } from './SettingsFormDialog/SettingsFormDialog';
import { NOTSET, ALLOW_ACCESS, DISALLOW_ACCESS, EDIT_BTN } from './constants';
import { INACTIVITY_TIMEOUT_HEADING } from '../../container/Settings/constants';

function getDisplayValue(field: SettingsField) {
  if (field.displayValue !== undefined) {
    return field.displayValue;
  }

  return field.value === true ? ALLOW_ACCESS : field.value === false ? DISALLOW_ACCESS : field.value;
}

interface SettingsSectionProps {
  title: string;
  fields: SettingsField[];
  resourceNotFound?: boolean;
  isEditAllowed: boolean;
  onSubmit: (formValues: Record<string, any>) => Promise<void>;
  dataTestId: string;
  showAlert?: boolean;
  alertMessage?: string;
  isLoading?: boolean;
}

const SettingsSection = ({
  title,
  fields,
  resourceNotFound = false,
  isEditAllowed,
  onSubmit,
  dataTestId,
  showAlert = false,
  alertMessage = '',
  isLoading = false
}: SettingsSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { classes } = useStyles();

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      <LuiCard
        headerBordered={true}
        title={title}
        titleActions={
          <Box>
            {!isLoading && isEditAllowed && (
              <IconButton aria-label={EDIT_BTN} size="small" onClick={handleDialogOpen}>
                <Edit />
              </IconButton>
            )}
          </Box>
        }
        className={classes.card}
        headerClass={classes.cardHeader}
        dataTestId={dataTestId}
        elevation={0}
      >
        {isLoading ? (<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant="text" animation={'pulse'} width={300} />
                <Skeleton variant="text" animation={'pulse'} width={300} />
              </Box>) : null}
        {!isLoading && fields.map(field => {
          const displayValue = resourceNotFound ? NOTSET : getDisplayValue(field);
          return !displayValue ? null : (
            <Box className={classes.cardField} key={field.name}>
              <Typography component="span">{field.label}: </Typography>
              <Typography
                component="span"
                className={resourceNotFound ? classes.titleNotSet : classes.titleMinutes}
                data-testid={`${field.name}-displayValue`}
              >
                {displayValue}
              </Typography>
            </Box>
          );
        })}
      </LuiCard>
      {isDialogOpen && (
        <SettingsFormDialog
          title={title}
          fields={fields}
          onSubmit={onSubmit}
          onClose={handleDialogClose}
          showAlert={showAlert}
          alertMessage={alertMessage}
          sizeDialog={title === INACTIVITY_TIMEOUT_HEADING ? "md" : "sm"}
        />
      )}
    </>
  );
};

export default SettingsSection;
